/**
 * PolicyAvbSection — the broker Police-detail "Versicherungsbedingungen (AVB)" surface
 * (Wave global-avb). The BROKER-app equivalent of the portal PortalPolicyAvbList: it
 * shows the AVB versions linked to this policy AND a control to run the auto-matching
 * engine (carrier + Sparte + validity) against the GLOBAL AVB library.
 *
 * Server state = TanStack Query ONLY (coding-standards §3): the linked-AVB list is a
 * query; "Auto-Zuordnung ausführen" is a mutation that, on success, refreshes the list.
 * The server owns every decision (matching, idempotency, provenance) — the UI only
 * triggers + renders (rule #2).
 *
 * DMS M12 (dms/spec.md §4.5, §M12): each linked AVB is now clickable ("AVB
 * öffnen", screenshot 04/07's second Dokumente-öffnen card) — it opens M11's
 * REUSED inline viewer (`/ablage/$id`) for that `avb_document_id`, same tab.
 * Navigation is an OPTIONAL `onOpenDocument` callback prop, not a direct
 * `useNavigate()` call: this section is unit-tested standalone (no
 * RouterProvider in PolicyAvbSection.test.tsx), so it stays router-agnostic —
 * `PoliceDetailScreen` (which DOES have router context) wires the callback.
 *
 * [Source: apps/web/src/portal/features/policies/PortalPolicyAvbList.tsx] (portal idiom)
 * [Source: apps/web/src/lib/api-client.ts] (policies.avbLinks / policies.avbAutoMatch)
 * [Source: projects/broker-ag/dms/spec.md §4.5, §M12]
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { BookOpen, ExternalLink, Sparkles } from 'lucide-react';
import { apiClient } from '../../lib/api-client';
import type { AvbLinkItem } from '../../lib/api-client';
import { Button } from '../../components/ui/Button';
import { dateShortDe } from '../../lib/format';

const avbLinksKey = (policyId: string) => ['policies', policyId, 'avb-links'] as const;

/** Gültigkeit label from a link's stored match reason (auto links only). */
function validityLabel(item: AvbLinkItem): string | null {
  const r = item.match_reason;
  if (!r) return null;
  const from = dateShortDe(r.avb_valid_from);
  return r.avb_valid_to ? `gültig ${from} - ${dateShortDe(r.avb_valid_to)}` : `gültig ab ${from}`;
}

export function PolicyAvbSection({
  policyId,
  onOpenDocument,
}: {
  policyId: string;
  /** DMS M12: called with `avb_document_id` on click — the caller navigates. */
  onOpenDocument?: (documentId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [runError, setRunError] = useState<string | null>(null);
  const [runSummary, setRunSummary] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<AvbLinkItem[]>({
    queryKey: avbLinksKey(policyId),
    queryFn: () => apiClient.policies.avbLinks(policyId),
  });

  const autoMatch = useMutation({
    mutationFn: () => apiClient.policies.avbAutoMatch(policyId),
    onSuccess: (result) => {
      setRunError(null);
      const total = result.matches.length;
      setRunSummary(
        total === 0
          ? 'Keine passende AVB in der globalen Bibliothek gefunden.'
          : `${result.created_count} neu zugeordnet, ${total} Treffer insgesamt.`,
      );
      void queryClient.invalidateQueries({ queryKey: avbLinksKey(policyId) });
    },
    onError: () => {
      setRunSummary(null);
      setRunError('Die automatische Zuordnung ist fehlgeschlagen.');
    },
  });

  return (
    <section aria-labelledby="police-avb" className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 id="police-avb" className="text-sm font-semibold text-foreground">
          Versicherungsbedingungen (AVB)
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => autoMatch.mutate()}
          disabled={autoMatch.isPending}
          data-testid="avb-auto-match"
        >
          <Sparkles aria-hidden />
          {autoMatch.isPending ? 'Zuordnung läuft…' : 'Auto-Zuordnung ausführen'}
        </Button>
      </div>

      {runSummary && <p className="mt-2 text-xs text-muted-foreground">{runSummary}</p>}
      {runError && (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {runError}
        </p>
      )}

      {isLoading && <p className="mt-3 text-sm text-muted-foreground">Lade AVB…</p>}
      {isError && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          Die Versicherungsbedingungen konnten nicht geladen werden.
        </p>
      )}

      {data && data.length === 0 && (
        <p className="mt-3 text-sm text-muted-foreground">
          Keine AVB zu dieser Police verknüpft. Nutzen Sie die Auto-Zuordnung.
        </p>
      )}

      {data && data.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="police-avb-list">
          {data.map((item) => {
            const validity = validityLabel(item);
            const inner = (
              <>
                <BookOpen className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{item.title}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {item.version_label}
                    {validity ? ` · ${validity}` : ''}
                    {item.carrier_name ? ` · ${item.carrier_name}` : ''}
                  </div>
                </div>
                {item.link_source === 'auto' && (
                  <span
                    className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground"
                    title="Automatisch zugeordnet"
                  >
                    Auto
                  </span>
                )}
                {onOpenDocument && (
                  <ExternalLink className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                )}
              </>
            );
            return (
              <li key={item.id}>
                {onOpenDocument ? (
                  // "AVB öffnen" (dms/spec.md §4.5, §M12) — opens M11's inline
                  // viewer for this AVB's document, same tab.
                  <button
                    type="button"
                    onClick={() => onOpenDocument(item.avb_document_id)}
                    className="flex w-full items-center gap-3 rounded-lg border bg-background px-4 py-3 text-left transition-colors hover:border-primary/50"
                  >
                    {inner}
                  </button>
                ) : (
                  <div className="flex items-center gap-3 rounded-lg border bg-background px-4 py-3">
                    {inner}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
